// @owners { team: patient-health }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import { ActionSheetContext, Column, H1, Illustration, SecondaryPage, XxxlPadding } from '@alto/design-system';
import { FamilyMemberContact, HolidayClosureAlert, Inbox, useGetSupportCases } from '@alto/features';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useEffect } from 'react';
import { logout } from '~shared/actions/auth';
import AltoAssistantImage from '~shared/assets/images/alto_assistant.png';
import { ASSISTANT_VIEWED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import { ASSISTANT_PAGE_ORIGINS } from '~shared/features/alto-assistant/constants';
import { useAnalytics } from '~shared/hooks';
import { useWundercomUnreadMessages } from '~shared/hooks/alto-assistant/useWundercomUnreadMessages';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared } from '~shared/store';
import { NewMessageCard } from './landing-page/NewMessageCard';
import { HolidayClosureActionSheet } from '~web/features/alto-assistant/components/action-sheets/HolidayClosureActionSheet';

export const LandingPage = () => {
  const queryClient = useQueryClient();
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const { trackPageView } = useAnalytics();
  const dispatch = useDispatchShared();
  const { isFamilyMember } = useGetSupportCases();

  useWundercomUnreadMessages();

  useEffect(() => {
    // Right now, users only arrive here after clicking the nav bar tab, but in the
    // future we'll probably want to support passing origin as a URL parameter.
    const origin = ASSISTANT_PAGE_ORIGINS.NAVIGATION_TAB;
    trackPageView({
      event: EVENTS.ASSISTANT_COMPONENT_VIEWED,
      params: {
        name: ASSISTANT_VIEWED_EVENTS.ASSISTANT_LANDING_PAGE,
        origin,
      },
    });
  }, [trackPageView]);

  if (isFamilyMember) {
    return (
      <FamilyMemberContact
        onLogoutPress={() => {
          dispatch(logout());
          queryClient.clear();
        }}
      />
    );
  }

  return (
    <SecondaryPage
      scrollEnabled={false}
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.SECONDARY_LIGHTEST}
      withoutWebFramingElementHeights
      HeaderContent={
        <XxxlPadding
          bottomPadding={SPACING.STATIC.SM}
          leftPadding={SPACING.STATIC.NONE}
          rightPadding={SPACING.STATIC.NONE}
        >
          <Column centerHorizontally>
            <Illustration
              size="xl"
              source={AltoAssistantImage}
              accessibilityLabel="Pharmacist"
            />
            <H1>How can we help?</H1>
          </Column>
        </XxxlPadding>
      }
    >
      <HolidayClosureAlert
        onPress={() => {
          setActiveActionSheet(<HolidayClosureActionSheet />);
        }}
      />
      <NewMessageCard />
      <Inbox />
    </SecondaryPage>
  );
};
